import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataService } from '../../services/dataService';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout } from './ChartLayout';
import './ChartLayout.css';
import { DataTable } from './DataTable';

Chart.register(...registerables);

export interface VMTMetrics {
  currentVMT: number;
  changeSince2005: number;
  californiaAverage: number;
  totalVMT2023: number;
}

// VMTPerCapitaMetrics interface removed - no longer needed

interface VMTChartProps {
  location: string;
  geofips: number | null;
  chartView?: 'total' | 'percapita';
  onMetricsCalculated?: (metrics: VMTMetrics) => void;
  onDataCalculated?: (data: any[]) => void;
  // Expose data table functionality for parent ChartLayout
  onDataTableReady?: (content: React.ReactNode, toggleFn: () => void) => void;
  // Expose download functionality for parent ChartLayout
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

export const VMTChart: React.FC<VMTChartProps> = ({ location, geofips, chartView = 'total', onMetricsCalculated, onDataCalculated, onDataTableReady, onDownloadPNGReady, onDownloadCSVReady }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [showDataTable, setShowDataTable] = useState(false);
  const [modifiedYears, setModifiedYears] = useState<Set<number>>(new Set());
  const chartCreationInProgress = useRef(false);


  // Function to calculate California average per capita VMT
  const calculateCaliforniaPerCapitaVMT = async (years: number[]) => {
    try {
      console.log('calculateCaliforniaPerCapitaVMT called with years:', years);
      
      // Fetch California population data
      const { data: californiaPopData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', 6) // California state FIPS code (no leading zero)
        .in('year', years);

      if (popError) {
        console.error('Error fetching California population data:', popError);
        return years.map(() => 0);
      }

      console.log('California population data:', californiaPopData);

      // Fetch raw Replica data for 2023 only
      console.log('Fetching California Replica data for 2023...');
      const { data: californiaReplicaData, error: replicaError } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('year, mode, vmt')
        .eq('geofips', 6)
        .eq('year', 2023)
        .in('mode', ['PRIVATE_AUTO', 'ON_DEMAND_AUTO']);

      if (replicaError) {
        console.error('Error fetching California Replica data:', replicaError);
      } else {
        console.log('California Replica data:', californiaReplicaData);
      }

      // Fetch AADT backcasted data for 2015-2022
      console.log('Fetching California AADT data for 2015-2022...');
      const { data: californiaAADTData, error: aadtError } = await supabaseClient
        .from('replica_vmt_by_mode')
        .select('year, mode, vmt')
        .eq('geofips', 6)
        .gte('year', 2015)
        .lte('year', 2022)
        .in('mode', ['PRIVATE_AUTO', 'ON_DEMAND_AUTO']);

      if (aadtError) {
        console.error('Error fetching California AADT data:', aadtError);
      } else {
        console.log('California AADT data:', californiaAADTData);
      }

      // Fetch EMFAC data for 2005-2014 scaling
      console.log('Fetching California EMFAC data for 2005-2015...');
      const { data: californiaEMFACData, error: emfacError } = await supabaseClient
        .from('emfac_final_estimation')
        .select('year, total_vmt')
        .eq('geofips', 6)
        .eq('class', 'LDV')
        .gte('year', 2005)
        .lte('year', 2015)
        .order('year', { ascending: true });

      if (emfacError) {
        console.error('Error fetching California EMFAC data:', emfacError);
      } else {
        console.log('California EMFAC data:', californiaEMFACData);
      }

      // Get 2015 VMT data as baseline for EMFAC scaling
      // First try AADT data, then fall back to EMFAC data
      let vmt2015Total = 0;
      const vmt2015AADTData = californiaAADTData?.filter(d => d.year === 2015) || [];
      if (vmt2015AADTData.length > 0) {
        vmt2015Total = vmt2015AADTData.reduce((sum, record) => sum + (record.vmt || 0), 0);
        console.log('California 2015 VMT from AADT data:', vmt2015Total);
      } else {
        // Fall back to EMFAC data for 2015 baseline
        const emfac2015Record = californiaEMFACData?.find(d => d.year === 2015);
        if (emfac2015Record) {
          vmt2015Total = emfac2015Record.total_vmt;
          console.log('California 2015 VMT from EMFAC data:', vmt2015Total);
        }
      }
      console.log('California 2015 VMT total for EMFAC baseline:', vmt2015Total);

      // Calculate per capita VMT for each year using the same approach as selected location
      // Exclude years 2005-2018 for California average (data shows zero, will fix later)
      // California average line should start from 2019
      const perCapitaData = years.map(year => {
        // Skip years 2005-2018 for California average - return null which Chart.js will not render
        if (year >= 2005 && year <= 2018) {
          console.log(`Skipping California average for year ${year} (data shows zero, will fix later)`);
          return null;
        }
        
        const yearPopData = californiaPopData?.find(d => d.year === year);
        if (!yearPopData) {
          console.log(`No population data for California year ${year}`);
          return null;
        }

        let totalVMT = 0;

        if (year === 2023) {
          // Use Replica data for 2023
          const yearReplicaData = californiaReplicaData?.filter(d => d.year === year) || [];
          totalVMT = yearReplicaData.reduce((sum, record) => sum + (record.vmt || 0), 0);
          console.log(`California 2023 VMT: ${totalVMT}`);
        } else if (year >= 2019 && year <= 2022) {
          // Use AADT backcasted data for 2019-2022 (2015-2018 excluded)
          const yearAADTData = californiaAADTData?.filter(d => d.year === year) || [];
          totalVMT = yearAADTData.reduce((sum, record) => sum + (record.vmt || 0), 0);
          console.log(`California ${year} AADT VMT: ${totalVMT}`);
        }

        const population = yearPopData.population || 1;
        const perCapita = (totalVMT / population) / 365; // Daily per capita VMT
        console.log(`California ${year}: VMT=${totalVMT}, Population=${population}, Per Capita=${perCapita.toFixed(2)}`);
        return perCapita;
      });

      console.log('California per capita VMT data:', perCapitaData);
      return perCapitaData;
    } catch (error) {
      console.error('Error calculating California per capita VMT:', error);
      return years.map(() => 0);
    }
  };

  // Effect to ensure chart is created after component mounts
  useEffect(() => {
    // This effect runs after the component mounts and the canvas is available
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const fetchVMTData = async () => {
      console.log('🚀 VMT Chart: Starting data fetch for', location, 'geofips:', geofips, 'chartView:', chartView);
      
      if (!location) {
        console.log('❌ VMT Chart: No location provided');
        return;
      }
      
      if (!geofips) {
        console.log('❌ VMT Chart: No geofips provided');
        setLoading(true);
        setError('Loading location data...');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch raw Replica data, AADT backcasted data, and EMFAC backcasting data
        
        // Fetch data with error handling for EMFAC
        let emfacData: any[] = [];
        let rawVmtData: any[] = [];
        let backcastedVmtData: any[] = [];
        
        try {
          
          const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
            DataService.getVMTData(location, undefined, geofips), // Raw Replica data using geofips
            DataService.getVMTDataWithScaling(geofips), // AADT backcasted data using geofips
            DataService.getEMFACBackcastingDataByGeofips(geofips).catch(err => {
              console.warn('EMFAC data not available, continuing without it:', err);
              return [];
            }) // EMFAC backcasting data using geofips
          ]);
          
          rawVmtData = rawVmtDataResult;
          backcastedVmtData = backcastedVmtDataResult;
          emfacData = emfacDataResult;
        } catch (error) {
          console.error('Error fetching VMT data:', error);
          setError('Failed to load VMT data');
          setLoading(false);
          return;
        }
        
        // Get population data for per capita calculations
        let population = 120000; // Default estimate
        try {
          const populationData = await supabaseLocationService.getLatestPopulation(location);
          if (populationData && populationData.population) {
            population = populationData.population;
          }
        } catch (err) {
          console.warn('Population data not available, using default:', err);
        }
        
        // Filter to only include PRIVATE_AUTO and ON_DEMAND modes for both datasets
        const rawPrivateAuto = rawVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const rawOnDemand = rawVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        const backcastedPrivateAuto = backcastedVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const backcastedOnDemand = backcastedVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        // Process EMFAC data for backcasting (2005-2014 only)
        
        // Get 2015 VMT data as the baseline for EMFAC backcasting
        const vmt2015PrivateAuto = backcastedPrivateAuto.find(d => d.year === 2015);
        const vmt2015OnDemand = backcastedOnDemand.find(d => d.year === 2015);
        const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
        
        
        // Create EMFAC backcasted data for years 2005-2014 only
        const emfacBackcastedData: any[] = [];
        const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
        
        if (vmt2015Total > 0 && emfacData.length > 0) {
          console.log('EMFAC data for backcasting:', emfacData);
          console.log('VMT 2015 total:', vmt2015Total);
          
          // Use the ratio data directly from EMFAC backcasting function
          for (const emfacRecord of emfacData) {
            if (emfacYears.includes(emfacRecord.year)) {
              const scaledVMT = vmt2015Total * emfacRecord.ratio;
              
              console.log(`EMFAC scaling for ${emfacRecord.year}: ratio=${emfacRecord.ratio}, scaledVMT=${scaledVMT}`);
              
              // Add backcasted data for both PRIVATE_AUTO and ON_DEMAND modes
              // Distribute proportionally based on 2015 ratios
              const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8; // Default to 80% private auto
              const onDemandRatio = 1 - privateAutoRatio;
              
              emfacBackcastedData.push({
                location_name: location,
                year: emfacRecord.year,
                mode: 'PRIVATE_AUTO',
                vmt: scaledVMT * privateAutoRatio
              });
              
              emfacBackcastedData.push({
                location_name: location,
                year: emfacRecord.year,
                mode: 'ON_DEMAND_AUTO',
                vmt: scaledVMT * onDemandRatio
              });
            }
          }
        } else {
          console.log('EMFAC backcasting skipped - vmt2015Total:', vmt2015Total, 'emfacData.length:', emfacData.length);
        }
        
        
        if (rawPrivateAuto.length === 0 && rawOnDemand.length === 0) {
          setError('No VMT data available for this location');
          setLoading(false);
          return;
        }

        // Get all available years (combine EMFAC backcasted years 2005-2014 with AADT backcasted years 2015-2023)
        const emfacPrivateAuto = emfacBackcastedData.filter(d => d.mode === 'PRIVATE_AUTO');
        const emfacOnDemand = emfacBackcastedData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        
        const allYears = Array.from(new Set([
          ...emfacPrivateAuto.map(d => d.year),
          ...emfacOnDemand.map(d => d.year),
          ...backcastedPrivateAuto.map(d => d.year),
          ...backcastedOnDemand.map(d => d.year)
        ])).sort();

        // Create a single dataset with different colors for different data sources
        const combinedData = allYears.map(year => {
          if (year === 2023) {
            // Use Replica data for 2023
            const privateAutoVmt = rawPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = rawOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          } else if (year >= 2005 && year <= 2014) {
            // Use EMFAC backcasted data for 2005-2014
            const privateAutoVmt = emfacPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = emfacOnDemand.find(d => d.year === year)?.vmt || 0;
            const totalVmt = privateAutoVmt + onDemandVmt;
            return totalVmt;
          } else {
            // Use AADT backcasted data for 2015-2022
            const privateAutoVmt = backcastedPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = backcastedOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          }
        });
        
        // Create background colors array - different colors for different data sources
        const backgroundColors = allYears.map(year => {
          if (year === 2023) return '#3b82f6'; // Blue for Replica data
          if (year >= 2005 && year <= 2014) return '#f59e0b'; // Orange for EMFAC backcasted data
          return '#10b981'; // Green for AADT backcasted data
        });
        
        const borderColors = allYears.map(year => {
          if (year === 2023) return '#1e40af'; // Dark blue for Replica data
          if (year >= 2005 && year <= 2014) return '#d97706'; // Dark orange for EMFAC backcasted data
          return '#047857'; // Dark green for AADT backcasted data
        });
        

        // Calculate per capita data for the right axis
        const perCapitaData = combinedData.map(vmt => vmt / population / 365);
        
        // Create datasets based on chart view
        let datasets: any[] = [];
        
        if (chartView === 'total') {
          // Total VMT view - only show VMT bars
          datasets = [
            {
              label: 'VMT Data',
              data: combinedData,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 1,
              yAxisID: 'y'
            }
          ];
        } else {
          // Per capita view - show line chart comparing location to California average
          // Calculate California average per capita VMT
          const californiaPerCapitaData = await calculateCaliforniaPerCapitaVMT(allYears);
          
          datasets = [
            {
              label: location,
              data: perCapitaData,
              type: 'line',
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: 6,
              pointHoverRadius: 8,
              tension: 0.4
            },
            {
              label: 'California Average',
              data: californiaPerCapitaData,
              type: 'line',
              borderColor: '#ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: (context: any) => {
                // Don't show points for null values (years 2005-2018)
                return (context.parsed.y === null || context.parsed.y === undefined) ? 0 : 6;
              },
              pointHoverRadius: 8,
              tension: 0.4,
              spanGaps: false // Don't draw lines through null values
            }
          ];
        }

        // Store chart data for download
        console.log('📊 VMT Chart: Creating download data with allYears:', allYears);
        console.log('📊 VMT Chart: Combined data length:', combinedData.length);
        
        const downloadData = allYears.map((year, index) => ({
          year: year,
          vmt: Math.round(combinedData[index]),
          data_source: year === 2023 ? 'Replica' : 
                     (year >= 2005 && year <= 2014) ? 'EMFAC Backcasted' : 'AADT Backcasted',
          location: location
        }));
        
        console.log('📊 VMT Chart: Download data created:', downloadData);
        setChartData(downloadData);
        setTableData(downloadData);
        setOriginalData([...downloadData]); // Store a copy of original data
        
        // Notify parent component of data change
        if (onDataCalculated) {
          console.log('📊 VMT Chart calling onDataCalculated with data:', downloadData);
          console.log('📊 VMT Chart data length:', downloadData.length);
          console.log('📊 VMT Chart data years:', downloadData.map(d => d.year));
          onDataCalculated(downloadData);
        } else {
          console.log('⚠️ VMT Chart: onDataCalculated callback not provided');
        }

        
        if (datasets.length === 0) {
          setError('No VMT data available for the requested modes');
          setLoading(false);
          return;
        }

        // Create chart with proper timing
        const createChart = () => {
          if (chartCreationInProgress.current) {
            return;
          }
          
          if (!chartRef.current) {
            setTimeout(createChart, 100);
            return;
          }
          
          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            setTimeout(createChart, 100);
            return;
          }
          
          chartCreationInProgress.current = true;

          // Destroy existing chart
          if (chartInstance.current) {
            chartInstance.current.destroy();
          }


          try {
            chartInstance.current = new Chart(ctx, {
              type: chartView === 'total' ? 'bar' : 'line',
              data: {
                labels: allYears.map(year => year.toString()),
                datasets: datasets
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: {
                  padding: {
                    left: 10,
                    right: 10,
                    top: 10,
                    bottom: 10
                  }
                },
                plugins: {
                  title: {
                    display: false
                  },
                  legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 10,
                      generateLabels: function(chart: any) {
                        const labels: any[] = [];
                        
                        if (chartView === 'total') {
                          // Total view - show data source legend
                          const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');
                          const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
                          const hasAadtData = backgroundColors.some(color => color === '#10b981');
                          
                          if (hasEmfacData) {
                            labels.push({
                              text: 'EMFAC Backcasted (2005-2014)',
                              fillStyle: '#f59e0b',
                              strokeStyle: '#f59e0b',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 0
                            });
                          }
                          if (hasAadtData) {
                            labels.push({
                              text: 'AADT Backcasted (2015-2022)',
                              fillStyle: '#10b981',
                              strokeStyle: '#10b981',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 1
                            });
                          }
                          if (hasReplicaData) {
                            labels.push({
                              text: 'Replica Data (2023)',
                              fillStyle: '#3b82f6',
                              strokeStyle: '#3b82f6',
                              lineWidth: 1,
                              pointStyle: 'rect' as const,
                              hidden: false,
                              index: 2
                            });
                          }
                        } else {
                          // Per capita view - show location and California average
                          labels.push({
                            text: location,
                            fillStyle: '#3b82f6',
                            strokeStyle: '#3b82f6',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 0
                          });
                          labels.push({
                            text: 'California Average',
                            fillStyle: '#ef4444',
                            strokeStyle: '#ef4444',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 1
                          });
                        }
                        return labels;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    grid: {
                      display: false
                    }
                  },
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: chartView === 'total' ? 'VMT' : 'Daily VMT per Capita (miles)',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    },
                    ticks: chartView === 'percapita' ? {
                      callback: function(value: any) {
                        return value.toFixed(1);
                      }
                    } : {
                      callback: function(value: any) {
                        // Format numbers with commas for thousands
                        if (typeof value === 'number') {
                          return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
                        }
                        return value;
                      }
                    }
                  }
                },
                interaction: {
                  intersect: false,
                  mode: 'index'
                }
              }
            });

            chartCreationInProgress.current = false;
          } catch (chartError) {
            console.error('Error creating chart:', chartError);
            setError('Failed to create chart');
            setLoading(false);
            chartCreationInProgress.current = false;
            return;
          }
        };

        // Start chart creation
        createChart();


        // Calculate metrics for the parent component
        if (onMetricsCalculated && (backcastedVmtData.length > 0 || emfacBackcastedData.length > 0)) {
          
          // Get population data from Supabase location service
          let population = 120000; // Default estimate
          try {
            const populationData = await supabaseLocationService.getLatestPopulation(location);
            if (populationData && populationData.population) {
              population = populationData.population;
            } else {
            }
          } catch (err) {
          }
          
          // Get the most recent year's data for current VMT
          const latestYear = Math.max(...allYears);
          let latestData: any[] = [];
          
          if (latestYear === 2023) {
            // Use raw Replica data for 2023
            latestData = rawVmtData.filter((d: any) => d.year === latestYear);
          } else if (latestYear >= 2005 && latestYear <= 2014) {
            // Use EMFAC backcasted data for 2005-2014
            latestData = emfacBackcastedData.filter((d: any) => d.year === latestYear);
          } else {
            // Use AADT backcasted data for 2015-2022
            latestData = backcastedVmtData.filter((d: any) => d.year === latestYear);
          }
          
          
          // Calculate VMT as PRIVATE_AUTO + ON_DEMAND_AUTO (personal vehicle usage)
          const privateAutoLatest = latestData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
          const onDemandLatest = latestData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
          const privateAutoComparison = backcastedVmtData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
          const onDemandComparison = backcastedVmtData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
          
          if (privateAutoLatest.length > 0) {
            // Calculate combined VMT for latest year (PRIVATE_AUTO + ON_DEMAND_AUTO)
            const privateAutoVMT = privateAutoLatest[0].vmt;
            const onDemandVMT = onDemandLatest.length > 0 ? onDemandLatest[0].vmt : 0;
            const currentVMT = privateAutoVMT + onDemandVMT;
            
            // Get 2005 data for comparison (earliest year with EMFAC backcasting)
            const earliestYear = Math.min(...allYears);
            const comparisonYear = allYears.includes(2005) ? 2005 : earliestYear;
            
            let privateAutoComparisonData: any[] = [];
            let onDemandComparisonData: any[] = [];
            
            if (comparisonYear >= 2005 && comparisonYear <= 2014) {
              // Use EMFAC backcasted data for comparison
              const emfacComparisonPrivateAuto = emfacBackcastedData.filter((d: any) => d.mode === 'PRIVATE_AUTO' && d.year === comparisonYear);
              const emfacComparisonOnDemand = emfacBackcastedData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO' && d.year === comparisonYear);
              privateAutoComparisonData = emfacComparisonPrivateAuto;
              onDemandComparisonData = emfacComparisonOnDemand;
            } else {
              // Use AADT backcasted data for comparison
              privateAutoComparisonData = privateAutoComparison.filter((d: any) => d.year === comparisonYear);
              onDemandComparisonData = onDemandComparison.filter((d: any) => d.year === comparisonYear);
            }
            
            const privateAutoComparisonVMT = privateAutoComparisonData.length > 0 ? privateAutoComparisonData[0].vmt : 0;
            const onDemandComparisonVMT = onDemandComparisonData.length > 0 ? onDemandComparisonData[0].vmt : 0;
            const vmtComparison = privateAutoComparisonVMT + onDemandComparisonVMT;
            
            
            // The VMT values appear to be annual total VMT, not per capita
            // We need to convert to daily per capita by:
            // 1. Dividing by population to get per capita
            // 2. Dividing by 365 to get daily values
            
            
            const estimatedPopulation = population;
            
            const currentVMTPerCapitaAnnual = currentVMT / estimatedPopulation;
            const vmtComparisonPerCapitaAnnual = vmtComparison / estimatedPopulation;
            
            // Convert annual VMT to daily per capita
            const currentVMTPerCapitaDaily = currentVMTPerCapitaAnnual / 365;
            const vmtComparisonPerCapitaDaily = vmtComparisonPerCapitaAnnual / 365;
            
            
            // Try to get actual California VMT data
            let californiaAverage = 15.8; // Default hardcoded value
            
            try {
              // Query for California state-level VMT data
              const { data: californiaVMTData, error: caError } = await supabaseClient
                .from('replica_vmt_by_mode')
                .select('*')
                .ilike('location_name', '%California%')
                .order('year', { ascending: false })
                .limit(10);
              
              if (!caError && californiaVMTData && californiaVMTData.length > 0) {
                
                // Get latest year California data
                const latestYear = Math.max(...californiaVMTData.map(d => d.year));
                const californiaLatest = californiaVMTData.filter(d => d.year === latestYear);
                
                // Calculate California VMT (PRIVATE_AUTO + ON_DEMAND_AUTO)
                const californiaPrivateAuto = californiaLatest.filter(d => d.mode === 'PRIVATE_AUTO');
                const californiaOnDemand = californiaLatest.filter(d => d.mode === 'ON_DEMAND_AUTO');
                
                const californiaPrivateAutoVMT = californiaPrivateAuto.length > 0 ? californiaPrivateAuto[0].vmt : 0;
                const californiaOnDemandVMT = californiaOnDemand.length > 0 ? californiaOnDemand[0].vmt : 0;
                const californiaTotalVMT = californiaPrivateAutoVMT + californiaOnDemandVMT;
                
                
                // Get California population from Supabase location service
                let californiaPopulation = 39000000; // Default estimate
                try {
                  const californiaData = await supabaseLocationService.getLatestPopulation('California');
                  if (californiaData && californiaData.population) {
                    californiaPopulation = californiaData.population;
                  } else {
                  }
                } catch (err) {
                }
                
                
                // Calculate California VMT per capita daily
                const californiaVMTPerCapitaDaily = (californiaTotalVMT / californiaPopulation) / 365;
                californiaAverage = californiaVMTPerCapitaDaily;
                
              } else {
              }
            } catch (err) {
            }
            
            
            // Calculate percentage change since comparison year
            const changeSinceComparison = vmtComparisonPerCapitaDaily > 0 ? ((currentVMTPerCapitaDaily - vmtComparisonPerCapitaDaily) / vmtComparisonPerCapitaDaily) * 100 : 0;
            
            // Get total VMT for 2023 from combinedData
            const vmt2023Index = allYears.indexOf(2023);
            const totalVMT2023 = vmt2023Index >= 0 ? combinedData[vmt2023Index] : 0;
            
            const metrics: VMTMetrics = {
              currentVMT: Math.round(currentVMTPerCapitaDaily * 10) / 10, // Round to 1 decimal place
              changeSince2005: Math.round(changeSinceComparison * 10) / 10, // Round to 1 decimal
              californiaAverage: Math.round(californiaAverage * 10) / 10, // Round to 1 decimal place
              totalVMT2023: Math.round(totalVMT2023) // Total VMT for 2023 in miles
            };
            
            onMetricsCalculated(metrics);
            
            // Per capita metrics calculation removed - no longer needed
            
            // Per capita data calculation removed - no longer needed
          } else {
          }
        } else {
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT data:', err);
        setError('Failed to load VMT data');
        setLoading(false);
      }
    };

    fetchVMTData();
  }, [location, geofips, chartView, onMetricsCalculated, onDataCalculated]);

  const toggleDataTable = useCallback(() => {
    setShowDataTable(!showDataTable);
  }, [showDataTable]);

  // Define handlers before early returns so they can be used in hooks
  const scaleDataToModifiedValue = useCallback((data: any[], modifiedYear: number, modifiedVMT: number) => {
    const originalModifiedData = originalData.find(d => d.year === modifiedYear);
    if (!originalModifiedData) return data;

    const scalingFactor = modifiedVMT / originalModifiedData.vmt;
    
    return data.map(item => {
      // Don't scale if this year has been manually modified
      if (modifiedYears.has(item.year)) {
        return item;
      }
      
      const originalItem = originalData.find(d => d.year === item.year);
      if (!originalItem) return item;
      
      return {
        ...item,
        vmt: Math.round(originalItem.vmt * scalingFactor)
      };
    });
  }, [originalData, modifiedYears]);

  // Define updateChart before handleDataTableChange to avoid temporal dead zone error
  const updateChart = useCallback((data: any[]) => {
    if (chartInstance.current && data.length > 0) {
      const sortedData = data.sort((a, b) => a.year - b.year);
      const years = sortedData.map(d => d.year);
      const vmtValues = sortedData.map(d => Math.round(d.vmt));
      
      // Check if data has been modified by comparing with original data
      const getDataSourceColor = (dataPoint: any) => {
        const originalPoint = originalData.find(orig => orig.year === dataPoint.year);
        
        // If no original data point exists, it's a new/custom entry
        if (!originalPoint) {
          return '#dc2626'; // Red for new data
        }
        
        // If this year was manually modified, use red color
        if (modifiedYears.has(dataPoint.year)) {
          return '#dc2626'; // Red for modified data
        }
        
        // If data source has been changed from original, it's custom
        if (originalPoint.data_source !== dataPoint.data_source) {
          return '#9333ea'; // Purple for custom data source
        }
        
        // Use original colors for unchanged or scaled data
        if (dataPoint.data_source === 'Replica') return '#3b82f6';
        if (dataPoint.data_source === 'EMFAC Backcasted') return '#f59e0b';
        return '#10b981'; // AADT Backcasted
      };

      const backgroundColors = sortedData.map(getDataSourceColor);
      const borderColors = backgroundColors.map(color => color);

      // Create datasets with proper legend labels
      const hasModifiedData = backgroundColors.some(color => color === '#dc2626');
      const hasCustomData = backgroundColors.some(color => color === '#9333ea');
      const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
      const hasAadtData = backgroundColors.some(color => color === '#10b981');
      const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');

      chartInstance.current.data = {
        labels: years,
        datasets: [{
          label: 'Vehicle Miles Traveled (Annual)',
          data: vmtValues,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        }]
      };

      // Update legend to show data source information
      if (chartInstance.current.options.plugins) {
        chartInstance.current.options.plugins.legend = {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels: function() {
            const labels: any[] = [];
            if (hasReplicaData) {
              labels.push({
                text: 'Replica (2023)',
                fillStyle: '#3b82f6',
                strokeStyle: '#3b82f6',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasEmfacData) {
              labels.push({
                text: 'EMFAC Backcasted (2005-2014)',
                fillStyle: '#f59e0b',
                strokeStyle: '#f59e0b',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasAadtData) {
              labels.push({
                text: 'AADT Backcasted (2015-2022)',
                fillStyle: '#10b981',
                strokeStyle: '#10b981',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasModifiedData) {
              labels.push({
                text: 'User Modified Values',
                fillStyle: '#dc2626',
                strokeStyle: '#dc2626',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            if (hasCustomData) {
              labels.push({
                text: 'Custom Data Source',
                fillStyle: '#9333ea',
                strokeStyle: '#9333ea',
                lineWidth: 1,
                pointStyle: 'rect' as const
              });
            }
            return labels;
          }
        }
        };
      }
      
      chartInstance.current.update('none'); // Update without animation for better performance
    }
  }, [originalData, modifiedYears]);

  const handleDataTableChange = useCallback((updatedData: any[]) => {
    // Track which years have been manually modified and find the changed value
    const newModifiedYears = new Set(modifiedYears);
    let modifiedYear: number | null = null;
    let modifiedVMT: number | null = null;
    
    // Find which year was just modified by comparing to current table data
    updatedData.forEach(newItem => {
      const currentItem = tableData.find(d => d.year === newItem.year);
      if (currentItem && Math.abs(newItem.vmt - currentItem.vmt) > 1) {
        newModifiedYears.add(newItem.year);
        modifiedYear = newItem.year;
        modifiedVMT = newItem.vmt;
      }
    });
    
    // Scale all unmodified years based on the modified value
    let finalData = updatedData;
    if (modifiedYear !== null && modifiedVMT !== null) {
      finalData = scaleDataToModifiedValue(updatedData, modifiedYear, modifiedVMT);
    }
    
    setModifiedYears(newModifiedYears);
    setTableData(finalData);
    setChartData(finalData);
    
    // Update chart with new data
    updateChart(finalData);
    
    // Notify parent component of data change
    if (onDataCalculated) {
      onDataCalculated(finalData);
    }
  }, [tableData, modifiedYears, originalData, scaleDataToModifiedValue, onDataCalculated, updateChart]);

  const handleRestoreDefaults = useCallback(() => {
    if (originalData.length > 0) {
      const restoredData = [...originalData];
      setTableData(restoredData);
      setChartData(restoredData);
      setModifiedYears(new Set()); // Reset modified years tracking
      
      // Update the chart with original data
      if (chartInstance.current) {
        const sortedData = restoredData.sort((a, b) => a.year - b.year);
        const years = sortedData.map(d => d.year);
        const vmtValues = sortedData.map(d => Math.round(d.vmt));
        
        // Create background colors based on original data sources (no custom data)
        const backgroundColors = sortedData.map(d => {
          if (d.data_source === 'Replica') return '#3b82f6';
          if (d.data_source === 'EMFAC Backcasted') return '#f59e0b';
          return '#10b981';
        });

        const borderColors = backgroundColors.map(color => color);

        chartInstance.current.data = {
          labels: years,
          datasets: [{
            label: 'Vehicle Miles Traveled (Annual)',
            data: vmtValues,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        };

        // Reset legend to original state (no custom data)
        const hasReplicaData = backgroundColors.some(color => color === '#3b82f6');
        const hasEmfacData = backgroundColors.some(color => color === '#f59e0b');
        const hasAadtData = backgroundColors.some(color => color === '#10b981');

        if (chartInstance.current.options.plugins) {
          chartInstance.current.options.plugins.legend = {
          display: true,
          position: 'bottom',
          labels: {
            generateLabels: function() {
              const labels: any[] = [];
              if (hasReplicaData) {
                labels.push({
                  text: 'Replica (2023)',
                  fillStyle: '#3b82f6',
                  strokeStyle: '#3b82f6',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              if (hasEmfacData) {
                labels.push({
                  text: 'EMFAC Backcasted (2005-2014)',
                  fillStyle: '#f59e0b',
                  strokeStyle: '#f59e0b',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              if (hasAadtData) {
                labels.push({
                  text: 'AADT Backcasted (2015-2022)',
                  fillStyle: '#10b981',
                  strokeStyle: '#10b981',
                  lineWidth: 1,
                  pointStyle: 'rect' as const
                });
              }
              return labels;
            }
          }
          };
        }
        
        chartInstance.current.update('none');
      }
      
      // Notify parent component of data change
      if (onDataCalculated) {
        onDataCalculated(restoredData);
      }
    }
  }, [originalData, onDataCalculated]);

  // Memoize data table content so it can be used in useEffect
  const dataTableContent = useMemo(() => {
    if (tableData.length === 0) return null;
    
    return (
      <div>
        {modifiedYears.size > 0 && (
          <div className="modified-years-info" style={{ marginBottom: '16px' }}>
            <small>
              <strong>Modified years:</strong> {Array.from(modifiedYears).sort().join(', ')} 
              (other years scaled proportionally)
            </small>
          </div>
        )}
        
        <DataTable
          data={tableData}
          highlightedRows={modifiedYears}
          highlightKey="year"
          columns={[
            { key: 'year', label: 'Year', type: 'year', editable: true, 
              validate: (value) => {
                const year = parseInt(value);
                if (isNaN(year) || year < 1990 || year > 2050) {
                  return 'Year must be between 1990 and 2050';
                }
                return null;
              }
            },
            { key: 'vmt', label: 'VMT (Annual)', type: 'number', editable: true,
              format: (value) => typeof value === 'number' ? Math.round(value).toLocaleString() : value,
              validate: (value) => {
                const vmt = parseFloat(value);
                if (isNaN(vmt) || vmt < 0) {
                  return 'VMT must be a positive number';
                }
                return null;
              }
            },
            { key: 'data_source', label: 'Data Source', type: 'text', editable: true },
            { key: 'location', label: 'Location', type: 'text', editable: false }
          ]}
          onDataChange={handleDataTableChange}
          onRestoreDefaults={handleRestoreDefaults}
          title="VMT Data"
          maxHeight="500px"
          pageSize={15}
        />
      </div>
    );
  }, [tableData, modifiedYears, handleDataTableChange, handleRestoreDefaults]);

  // Download handlers
  const handleDownloadPNG = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Chart_${viewLabel}`);
    downloadChartAsPNG(chartRef, filename);
  }, [chartView, location]);

  const handleDownloadCSV = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Data_${viewLabel}`);
    downloadDataAsCSV(chartData, filename);
  }, [chartView, location, chartData]);

  // Expose data table content and toggle function to parent ChartLayout
  useEffect(() => {
    if (onDataTableReady && dataTableContent) {
      onDataTableReady(dataTableContent, toggleDataTable);
    }
  }, [dataTableContent, onDataTableReady, toggleDataTable]);

  // Expose download handlers to parent ChartLayout
  useEffect(() => {
    if (onDownloadPNGReady) {
      onDownloadPNGReady(handleDownloadPNG);
    }
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    if (onDownloadCSVReady) {
      onDownloadCSVReady(handleDownloadCSV);
    }
  }, [onDownloadCSVReady, handleDownloadCSV]);

  // Cleanup effect to destroy charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading VMT data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
        <div>{error}</div>
      </div>
    );
  }

  const chartContent = (
    <div style={{ height: '400px', width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Return just the chart content - ChartLayout wrapper is handled by Transportation.tsx
  return (
    <div className="vmt-chart-container">
      {chartContent}
    </div>
  );
};
